import { characteristics } from '../data/characteristics';
import type { Score, Category } from '../types';

const categoryLabels: Record<Category, string> = {
  being: 'Being',
  doing: 'Doing',
  having: 'Having',
};

const categoryAliases: Record<Category, string[]> = {
  being: ['being'],
  doing: ['doing'],
  having: ['having'],
};

function normalizeText(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s%]/g, ' ').replace(/\s+/g, ' ').trim();
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateAIAnalysis(
  aiText: string,
  scores: Score[],
): ValidationResult {
  const errors: string[] = [];

  const scoreMap = new Map<string, number>();
  for (const score of scores) {
    scoreMap.set(score.characteristicId, Math.round(score.normalized));
  }

  const normalized = normalizeText(aiText);

  for (const char of characteristics) {
    const expectedScore = scoreMap.get(char.id);
    if (expectedScore === undefined) continue;

    const charLower = char.name.toLowerCase();

    let searchStart = 0;
    let found = false;

    while (!found) {
      const pos = normalized.indexOf(charLower, searchStart);
      if (pos === -1) break;

      const after = normalized.substring(pos + charLower.length);
      const scoreMatch = after.match(/^\s{0,5}(\d+)\s*%/);

      if (scoreMatch) {
        const mentionedScore = parseInt(scoreMatch[1], 10);
        if (mentionedScore !== expectedScore) {
          errors.push(
            `Score mismatch for ${char.name}: the assessment score is ${expectedScore}% but the AI analysis states ${mentionedScore}%.`,
          );
        }

        const before = normalized.substring(0, pos);
        const allCategories: Category[] = ['being', 'doing', 'having'];
        let bestCategory: Category | null = null;
        let bestIndex = -1;

        for (const cat of allCategories) {
          for (const alias of categoryAliases[cat]) {
            const aliasIndex = before.lastIndexOf(alias);
            if (aliasIndex > bestIndex) {
              bestIndex = aliasIndex;
              bestCategory = cat;
            }
          }
        }

        if (bestCategory && bestCategory !== char.category) {
          errors.push(
            `Category mismatch for ${char.name}: it belongs under ${categoryLabels[char.category]} but the AI analysis places it under ${categoryLabels[bestCategory]}.`,
          );
        }

        found = true;
      }

      searchStart = pos + charLower.length;
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
